import React, { useState } from 'react';
import { Button, useListContext, useRefresh, useNotify, useUnselectAll, useTranslate } from 'react-admin';
import SendIcon from '@mui/icons-material/Send';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button as MuiButton,
} from '@mui/material';

import { apiPost } from '../api-client/apiClient';

export const TelegramProductBulkActions = () => {
    const { selectedIds } = useListContext();
    const refresh = useRefresh();
    const notify = useNotify();
    const unselectAll = useUnselectAll('telegram-products');
    const translate = useTranslate();
    const [priceDialogOpen, setPriceDialogOpen] = useState(false);
    const [priceAdjustmentType, setPriceAdjustmentType] = useState<'increase' | 'decrease'>('increase');
    const [percentage, setPercentage] = useState('');

    const handleBulkPost = async () => {
        try {
            const data = await apiPost<{ results: { success: boolean }[] }>(
                '/telegram-products/bulk-post',
                { ids: selectedIds },
            );
            const successCount = data.results.filter((r) => r.success).length;
            notify(translate('app.telegram.notifications.posted', { count: successCount }), { type: 'success' });
            unselectAll();
            refresh();
        } catch {
            notify(translate('app.telegram.notifications.postError'), { type: 'error' });
        }
    };

    const handleBulkUnpost = async () => {
        try {
            const data = await apiPost<{ results: { success: boolean }[] }>(
                '/telegram-products/bulk-unpost',
                { ids: selectedIds },
            );
            const successCount = data.results.filter((r) => r.success).length;
            notify(translate('app.telegram.notifications.unposted', { count: successCount }), { type: 'success' });
            unselectAll();
            refresh();
        } catch {
            notify(translate('app.telegram.notifications.unpostError'), { type: 'error' });
        }
    };

    const openPriceDialog = (type: 'increase' | 'decrease') => {
        setPriceAdjustmentType(type);
        setPercentage('');
        setPriceDialogOpen(true);
    };

    const handlePriceAdjust = async () => {
        const percentageValue = parseFloat(percentage);
        if (isNaN(percentageValue) || percentageValue <= 0) {
            notify(translate('app.telegram.notifications.invalidPercentage'), { type: 'error' });
            return;
        }

        const adjustedPercentage = priceAdjustmentType === 'increase' ? percentageValue : -percentageValue;

        try {
            const data = await apiPost<{ results: { success: boolean }[] }>(
                '/telegram-products/bulk-adjust-price',
                { ids: selectedIds, percentage: adjustedPercentage },
            );
            const successCount = data.results.filter((r) => r.success).length;
            notify(translate('app.telegram.notifications.priceAdjusted', { action: translate(priceAdjustmentType === 'increase' ? 'app.bulk.increase' : 'app.bulk.decrease'), count: successCount, percentage: percentageValue }), { type: 'success' });
            setPriceDialogOpen(false);
            unselectAll();
            refresh();
        } catch {
            notify(translate('app.telegram.notifications.priceAdjustError'), { type: 'error' });
        }
    };

    return (
        <>
            <Button label={translate('app.telegram.postToTg')} onClick={handleBulkPost}>
                <SendIcon />
            </Button>
            <Button label={translate('app.telegram.unpostFromTg')} onClick={handleBulkUnpost}>
                <UnpublishedIcon />
            </Button>
            <Button label={translate('app.bulk.increasePrice')} onClick={() => openPriceDialog('increase')}>
                <TrendingUpIcon />
            </Button>
            <Button label={translate('app.bulk.decreasePrice')} onClick={() => openPriceDialog('decrease')}>
                <TrendingDownIcon />
            </Button>

            <Dialog open={priceDialogOpen} onClose={() => setPriceDialogOpen(false)}>
                <DialogTitle>
                    {priceAdjustmentType === 'increase' ? translate('app.bulk.increase') : translate('app.bulk.decrease')} {translate('resources.products.fields.price')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={translate('app.bulk.percentage')}
                        type="number"
                        fullWidth
                        value={percentage}
                        onChange={(e) => setPercentage(e.target.value)}
                        InputProps={{ inputProps: { min: 0 } }}
                    />
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={() => setPriceDialogOpen(false)}>{translate('app.cancel')}</MuiButton>
                    <MuiButton onClick={handlePriceAdjust} variant="contained">
                        {priceAdjustmentType === 'increase' ? translate('app.bulk.increase') : translate('app.bulk.decrease')}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </>
    );
};
